/**
 * 格式化和工具函数集合
 */

/**
 * 格式化价格为人民币显示
 * @param {number} price - 价格数字
 * @returns {string} 格式化后的价格字符串，如 "¥12.50"
 */
export function formatPrice(price) {
  return `¥${price}`
}

/**
 * 格式化数量为显示格式
 * @param {number} qty - 数量
 * @returns {string} 格式化后的数量字符串，如 "1+", "10+", "1K+"
 */
export function formatQuantity(qty) {
  if (qty >= 1000) {
    return `${(qty / 1000).toFixed(1)}K+`
  }
  return `${qty}+`
}

/**
 * 根据库存数量判断库存状态
 * @param {number} available - 可用库存数
 * @returns {string} 库存状态: 'in-stock' | 'low-stock' | 'out-of-stock'
 */
export function getStockStatus(available) {
  if (available === 0) {
    return 'out-of-stock'
  }
  if (available < 100) {
    return 'low-stock'
  }
  return 'in-stock'
}

/**
 * 获取库存状态的文字描述
 * @param {string} status - 库存状态
 * @returns {string} 状态文字，如 "现货充足", "库存紧张"
 */
export function getStockText(status) {
  const texts = {
    'in-stock': '现货充足',
    'low-stock': '库存紧张',
    'out-of-stock': '暂时缺货'
  }
  return texts[status] || '未知'
}

/**
 * 获取库存状态对应的颜色类名
 * @param {string} status - 库存状态
 * @returns {string} 颜色类名
 */
export function getStockColor(status) {
  const colors = {
    'in-stock': 'success',      // 绿色
    'low-stock': 'warning',     // 橙色
    'out-of-stock': 'muted'     // 灰色
  }
  return colors[status] || 'muted'
}

/**
 * 格式化商品总价
 * @param {number} price - 单价
 * @param {number} quantity - 数量
 * @returns {string} 格式化后的总价，如 "¥125.00"
 */
export function formatTotalPrice(price, quantity) {
  return formatPrice(price * quantity)
}

/**
 * 获取标签的显示文字
 * @param {string} tag - 标签值: 'New' | 'Hot' | ''
 * @returns {string} 标签文字
 */
export function getTagText(tag) {
  const tags = {
    'New': '新品',
    'Hot': '热销',
    '': ''
  }
  return tags[tag] || tag
}

/**
 * 复制文本到剪贴板
 * @param {string} text - 要复制的文本
 * @returns {Promise<boolean>} 复制是否成功
 */
export async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch (err) {
    console.error('复制失败:', err)
    return false
  }
}

/**
 * 显示复制成功的提示
 * @param {string} text - 已复制的内容描述，如 "已复制型号"
 */
export function showCopySuccess(text) {
  // 简单实现，可扩展为 Toast 组件
  alert(`${text}到剪贴板`)
}

/**
 * 生成产品的 SEO 友好的 slug
 * @param {string} model - 产品型号
 * @param {string} name - 产品名称
 * @returns {string} URL 友好的 slug
 */
export function generateProductSlug(model, name) {
  const slug = `${model}-${name}`
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
  return slug
}

/**
 * 格式化日期为中文格式
 * @param {Date|string} date - 日期对象或字符串
 * @returns {string} 格式化后的日期，如 "2025-12-29"
 */
export function formatDate(date) {
  const d = new Date(date)
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * 截断文本，超过长度显示省略号
 * @param {string} text - 原文本
 * @param {number} length - 截断长度
 * @returns {string} 截断后的文本
 */
export function truncateText(text, length = 100) {
  if (!text || text.length <= length) {
    return text
  }
  return text.substring(0, length) + '...'
}

/**
 * 将对象转换为 URL 查询字符串
 * @param {Object} params - 参数对象
 * @returns {string} URL 查询字符串，如 "page=1&limit=10"
 */
export function objectToQueryString(params) {
  return Object.entries(params)
    .filter(([, value]) => value !== null && value !== undefined && value !== '')
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&')
}

/**
 * 解析 URL 查询参数
 * @param {string} queryString - URL 查询字符串
 * @returns {Object} 参数对象
 */
export function parseQueryString(queryString) {
  const params = {}
  if (!queryString) return params

  const pairs = queryString.split('&')
  pairs.forEach(pair => {
    const [key, value] = pair.split('=')
    if (key) {
      params[decodeURIComponent(key)] = value ? decodeURIComponent(value) : ''
    }
  })
  return params
}
